import { Flex } from "@components/index";
import { Icons } from "@components/internal/Icons";
import type { StatusGanttVariantType } from "@components/internal/StatusGantt/StatusGantt.component";
import LateIcon from "@fragments/LateIcon.component";
import { useChannelIcons } from "@src/common/hooks";
import { useLayoutCollapseContext } from "@template/contexts/LayoutCollapse.context";
import { memo, useMemo } from "react";
import { CollapseButton } from "../CollapseButton";
import styles from "./FooterTimeline.module.scss";

interface LegendItem {
  label: string;
  status: StatusGanttVariantType;
}

const STATUS_LEGENDS: readonly LegendItem[] = [
  { label: "Não iniciado", status: "not-started" },
  { label: "Finalizado", status: "completed" },
  { label: "Em andamento", status: "in-progress" },
  { label: "Erro", status: "error" },
  { label: "Não executado", status: "not-executed" },
  { label: "Aguardando justificativa", status: "awaiting-justification" },
] as const;

interface LegendItemProps {
  label: string;
  status: StatusGanttVariantType;
}

const LegendItemComponent: React.FC<LegendItemProps> = memo(
  ({ label, status }) => {
    const statusClass = styles[`rectangle-${status}`];

    return (
      <div className={styles.item}>
        <div className={`${styles.rectangle} ${statusClass}`} />
        <span className={styles.label}>{label}</span>
      </div>
    );
  }
);

LegendItemComponent.displayName = "LegendItem";

const Divider: React.FC = memo(() => <hr className={styles.divider} />);

Divider.displayName = "Divider";

const FooterTimeline: React.FC = () => {
  const { getChannelLegends } = useChannelIcons();
  const { isLegendCollapsed, toggleLegend } = useLayoutCollapseContext();

  const channelLegends = useMemo(
    () => getChannelLegends(),
    [getChannelLegends]
  );

  const contentClass = isLegendCollapsed
    ? `${styles.content} ${styles["content-collapsed"]}`
    : styles.content;

  return (
    <Flex.Row alignItems="center" gap="12px" className={styles.wrapper}>
      <div className={contentClass}>
        <span className={styles.title}>Legenda:</span>

        {STATUS_LEGENDS.map(({ label, status }) => (
          <LegendItemComponent key={status} label={label} status={status} />
        ))}

        <Divider />

        <Flex.Row gap="4px" alignItems="center">
          <LateIcon size="sm" />
          <span className={styles.label}>Atrasado</span>
        </Flex.Row>

        <Flex.Row gap="4px" alignItems="center">
          <Icons name="bookmark-fill" />
          <span className={styles.label}>Processo crítico</span>
        </Flex.Row>

        <Divider />

        {channelLegends && <div>{channelLegends}</div>}
      </div>

      <CollapseButton
        isCollapsed={isLegendCollapsed}
        onClick={toggleLegend}
        direction="down"
        className={
          isLegendCollapsed
            ? `${styles["collapse-btn"]} ${styles["collapse-btn-up"]}`
            : styles["collapse-btn"]
        }
      />
    </Flex.Row>
  );
};

FooterTimeline.displayName = "FooterTimeline";

export default memo(FooterTimeline);
